import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EOrdenacao } from '../../../shared/data/ordenacao.enum';
import { IDevice, IKioskRegistration, IListByFilterDevices } from '../models/device.interface';
import { SessionService } from '../../../shared/services/session.service';
import { DeviceService } from '../services/device.service';
import { UntypedFormControl } from '@angular/forms';
import { ICompany } from '../../companies/models/company.interface';
import { CompanyService } from '../../companies/services/company.service';
import { canPickCompany } from '../../../shared/utilities/company-scope';
import { statusFilterToActive } from '../../../shared/utilities/status-filter';
import { EMessageType } from '../../../shared/data/response-default.interface';
import { interceptorAvisouErro } from '../../../shared/http-interceptor/http-interceptor.module';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss']
})
export class DevicesComponent implements OnInit, OnDestroy {
  // Usuário de empresa filtra sempre pela própria (companyId da sessão em loadDevices), sem seletor.
  podeEscolherEmpresa = false;
  private readonly destroy$ = new Subject<void>();
  devices: IDevice[] = [];
  kioskRegistrations: Record<string, IKioskRegistration> = {};
  private devicesSub?: Subscription;
  private kioskSub?: Subscription;
  // Empresa dos registros carregados; undefined = ainda não carregou (ou falhou).
  private kioskCompanyId?: string | null;
  dadosPaginacao = {
    ordenacao: EOrdenacao.AlfabeticaAZ,
    paginaSelecionada: 0,
    tamanhoPagina: 10,
    totalPaginas: 0,
    itensTotal: 0
  };
  formInput = new UntypedFormControl();
  orderControl = new UntypedFormControl(1);
  companyControl = new UntypedFormControl();
  statusControl = new UntypedFormControl('all');
  listOrder = [
    {
      value: EOrdenacao.AlfabeticaAZ,
      label: 'Alfabética - de A a Z'
    },
    {
      value: EOrdenacao.AlfabeticaZA,
      label: 'Alfabética - de Z a A'
    },
    {
      value: EOrdenacao.MaisNovos,
      label: 'Mais novos'
    },
    {
      value: EOrdenacao.MaisAntigos,
      label: 'Mais antigos'
    }
  ];

  companies: ICompany[] = [];

  constructor(
    private readonly deviceService: DeviceService,
    private readonly sessionService: SessionService,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.podeEscolherEmpresa = canPickCompany(this.sessionService.getSession());
    this.getDevices();
    this.orderControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((orderSelected: EOrdenacao) => {
      this.dadosPaginacao.ordenacao = orderSelected;
      this.recarregarDoInicio();
    });

    // Usuário de empresa não tem seletor, e Company/ListActive responde 403 para ele.
    if (this.podeEscolherEmpresa) {
      this.companyService
        .listarEmpresasAtivas()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.companies = response.data;
          },
          error: (erro) => {
            this.companies = [];
            if (!interceptorAvisouErro(erro)) {
              this.toastService.toast('Erro ao carregar as empresas. Tente novamente.', EMessageType.Error);
            }
          }
        });
    }

    this.companyControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recarregarDoInicio();
    });

    this.statusControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recarregarDoInicio();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Recarga completa (início, revogação, cadastro): devices e registros de totem. */
  public getDevices(): void {
    this.loadDevices(true);
  }

  private loadDevices(reloadRegistrations = false): void {
    const companyId = this.sessionService.getSession().companyId;

    const filter: IListByFilterDevices = {
      pageNumber: this.dadosPaginacao.paginaSelecionada,
      pageSize: this.dadosPaginacao.tamanhoPagina,
      orderBy: this.dadosPaginacao.ordenacao
    };

    const companyIdFilter = companyId || this.companyControl.value;

    if (companyIdFilter) {
      filter.companyId = String(companyIdFilter);
    }

    if (this.formInput.value) {
      filter.searchText = String(this.formInput.value);
    }

    filter.active = statusFilterToActive(this.statusControl.value);

    // Cancela a requisição anterior: uma resposta atrasada de outro filtro ou página não sobrescreve a atual.
    this.devicesSub?.unsubscribe();
    this.devicesSub = this.deviceService
      .listDevices(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resposta) => {
          this.devices = resposta.data;
          this.dadosPaginacao.totalPaginas = Math.ceil(resposta.count! / this.dadosPaginacao.tamanhoPagina);
          this.dadosPaginacao.itensTotal = resposta.count!;
        },
        error: (erro) => {
          // Lista vazia em vez de dados de outro filtro ou página.
          this.devices = [];
          this.dadosPaginacao.totalPaginas = 0;
          this.dadosPaginacao.itensTotal = 0;
          if (!interceptorAvisouErro(erro)) {
            this.toastService.toast('Não foi possível carregar os dispositivos.', EMessageType.Error);
          }
        }
      });

    // Os registros só mudam com outra empresa ou depois de revogar/cadastrar: página e ordenação não recarregam.
    const registrationsCompanyId = filter.companyId ?? null;
    if (reloadRegistrations || registrationsCompanyId !== this.kioskCompanyId) {
      this.loadKioskRegistrations(registrationsCompanyId);
    }
  }

  private loadKioskRegistrations(companyId: string | null): void {
    this.kioskCompanyId = companyId;
    this.kioskSub?.unsubscribe();
    // Falha aqui não deve esconder a lista de dispositivos: a coluna só fica vazia e a próxima carga tenta de novo.
    this.kioskSub = this.deviceService
      .listKioskRegistrations(companyId ?? undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resposta) => {
          this.kioskRegistrations = Object.fromEntries(resposta.data.map((registro) => [registro.deviceId, registro]));
        },
        error: () => {
          this.kioskRegistrations = {};
          this.kioskCompanyId = undefined;
        }
      });
  }

  public cleanForm(): void {
    this.formInput.reset();
    this.companyControl.reset(null, { emitEvent: false });
    this.statusControl.reset('all', { emitEvent: false });
    this.recarregarDoInicio();
  }

  public filterDevice(): void {
    this.recarregarDoInicio();
  }

  public mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;
    this.recarregarDoInicio();
  }

  /** Mudou um filtro, a ordenação ou o tamanho da página: a página atual pode não existir mais. */
  private recarregarDoInicio(): void {
    this.dadosPaginacao.paginaSelecionada = 0;
    this.loadDevices();
  }

  public mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;
    this.loadDevices();
  }
}
